import { BaseStore } from './baseStore';
import { Store } from './store';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { genID } from '../utils/snowflake';
import { SignalProtocolAddress } from '@privacyresearch/libsignal-protocol-typescript';
import { arrayBufferToBase64, stringToArrayBuffer } from '../utils/global';

export enum ChannelStoreState {
  NOT_INITIALIZED = 1 << 0,
  RECEIVED_CHANNELS = 1 << 1,
}

export class ChannelStore extends BaseStore<ChannelStoreState> {
  channels: any[];
  activeChannel: any | null;
  messages: Map<number, any[]>;

  constructor(store: Store) {
    super(ChannelStoreState.NOT_INITIALIZED, store, 'blue');
    makeObservable(this, {
      channels: observable,
      activeChannel: observable,
      messages: observable,
      _setChannels: action,
      setActiveChannel: action,
      addMessage: action,
      sendMessage: action,
      updateChannel: action,
      removeChannel: action,
    });
    this.reload();
    this.channels = [];
    this.activeChannel = null;
    this.messages = new Map();
    this.log('Init ChannelStore');
  }

  isReady(): boolean {
    return this.hasState(ChannelStoreState.RECEIVED_CHANNELS);
  }

  logout(): void {
    this.channels = [];
    this.setState(ChannelStoreState.NOT_INITIALIZED);
  }

  reload(): void {
    this.channels = [];
    if (this.store.token == null) return;
    this.store.socket.ws.on('channel:list', data => {
      this.log('Received channel list from socket');
      this.store!.channels._setChannels(data);
      this.store!.channels.addState(ChannelStoreState.RECEIVED_CHANNELS);
    });

    this.store.socket.ws.on('channel:update', data => {
      this.log('Received channel update from socket', data);
      try {
        if (data && data.id) this.store!.channels.updateChannel(data);
      } catch (e) {
        this.log('Failed to apply channel update', e);
      }
    });

    this.store.socket.ws.on('channel:deleted', data => {
      this.log('Received channel deleted from socket', data);
      try {
        if (data && data.id) this.store!.channels.removeChannel(Number(data.id));
      } catch (e) {
        this.log('Failed to apply channel deleted', e);
      }
    });

    this.store.socket.ws.on('channel:removed', data => {
      this.log('Received channel removed for this user', data);
      try {
        if (data && data.id) this.store!.channels.removeChannel(Number(data.id));
      } catch (e) {
        this.log('Failed to apply channel removed', e);
      }
    });

    this.store.socket.ws.on('channel:update', data => {
      this.log('Received channel update from socket', data);
      try {
        if (data && data.id) this.store!.channels.updateChannel(data);
      } catch (e) {
        this.log('Failed to apply channel update', e);
      }
    });

    this.store.socket.ws.on('channel:message', data => {
      this.log('Received channel message from socket', data);
      try {
        this.handleChannelMessage(data);
      } catch (e) {
        this.log('Failed to handle channel:message', e);
      }
    });
    this.setState(ChannelStoreState.NOT_INITIALIZED);
  }

  private handleChannelMessage(data: any): void {
    if (!data) return;
    const channelId = Number(data.channelId ?? data.channel);
    if (!channelId) return;
    const list = this.store!.channels.messages.get(channelId) || [];

    const eq = (a: any, b: any) => a != null && b != null && a == b;
    const idx = list.findIndex((m: any) => eq(m.clientId, data.clientId) || eq(m.id, data.id));
    if (idx !== -1) {
      list[idx] = data;
      this.store!.channels.messages.set(channelId, list);
      return;
    }

    const currentUserId = this.store!.user.user?.id;
    const isFromCurrentUser = data.author === currentUserId;
    const isCurrentChannel = this.store!.channels.activeChannel?.id === channelId;

    if (!isFromCurrentUser && !isCurrentChannel) {
      this.store.socket.showMessageNotification(data, channelId);
    }

    this.store!.channels.addMessage(channelId, data);
  }

  _setChannels(channels: any[]) {
    this.log('Set channels', channels);
    this.channels = channels;
  }

  async setActiveChannel(channel: any) {
    this.log('Set active channel', channel?.id);
    const prev = this.activeChannel;
    this.activeChannel = channel;

    if (channel && !this.messages.has(channel.id)) {
      runInAction(() => {
        this.messages.set(channel.id, []);
      });
      //if channel is dm use e2ee
      if (channel.type == 'dm') {
        const targetUserId = channel.members.find(e => e !== this.store.user.user.id);
        console.log(targetUserId)
        const hasSession = await this.store.e2ee.protocolStore.containsSession(
          new SignalProtocolAddress(targetUserId, 1).toString()
        );

        if (!hasSession) {
          try {
            const targetBundle = await this.store.e2ee.fetchUserKeys(targetUserId);
            await this.store.e2ee.initSession(targetUserId, targetBundle, 1);
          } catch (err) {
            this.log("❌ Brak kluczy dla usera", targetUserId, err);
          }
        }
      }
      this.loadMessages(channel.id);
    }

    try {
      const ws = this.store.socket.ws;
      if (ws && ws.connected) {
        if (prev?.id) ws.emit('channel:leave', { channelId: prev.id });
        if (channel?.id) ws.emit('channel:join', { channelId: channel.id });
      }
    } catch (e) {
      this.log('Failed to emit channel join/leave', e);
    }
  }

  async loadMessages(channelId: number) {
    try {
      const res = await fetch(`${this.store.apiURL}/channels/${channelId}/messages`, {
        headers: { Authorization: `Bearer ${this.store.token}` },
      });
      if (!res.ok) return;

      const data = await res.json();
      const rawMsgs = data?.data ?? data;

      // 🔐 Odszyfrowanie wiadomości E2EE (jeśli hashContent istnieje)
      const msgs = await Promise.all(
        rawMsgs.map(async (msg: any) => {
          if (msg.hashContent) {
            try {
              const plaintext = await this.store.e2ee.receiveMessage(
                msg.author.toString(),
                1,
                {
                  type: msg.hashContent.type,
                  body: msg.hashContent.body, // 👈 to jest base64 string
                }
              );

              return {
                ...msg,
                content: plaintext, // nadpisujemy content odszyfrowanym tekstem
              };
            } catch (err) {
              this.log(`❌ Failed to decrypt message ${msg.id}`, err);
              return {
                ...msg,
                content: "[decrypt error]",
              };
            }
          }
          return msg; // zwykłe wiadomości bez E2EE
        })
      );

      runInAction(() => {
        this.messages.set(channelId, msgs);
      });
    } catch (e) {
      this.log("Failed to load messages", e);
    }
  }


  addMessage(channelId: number, message: any) {
    const list = this.messages.get(channelId) || [];
    if (message) {
      const exists = list.find((m: any) => {
        if (message.clientId && m.clientId && String(m.clientId) === String(message.clientId))
          return true;
        if (
          typeof message.id !== 'undefined' &&
          typeof m.id !== 'undefined' &&
          String(m.id) === String(message.id)
        )
          return true;
        return false;
      });
      if (exists) return;
    }
    list.push(message);
    runInAction(() => {
      this.messages.set(channelId, list);
    });
  }

  async loadChannel(channelId: number) {
    try {
      const res = await fetch(`${this.store.apiURL}/channels/${channelId}`, {
        headers: { Authorization: `Bearer ${this.store.token}` },
      });
      if (!res.ok) return null;
      const data = await res.json();
      const channel = data?.data ?? data;

      if (channel && !this.getChannel(channelId)) {
        this._setChannels([channel, ...this.channels]);
      }

      this.setActiveChannel(channel);
      return channel;
    } catch (e) {
      this.log('Failed to load channel', e);
      return null;
    }
  }

  async createDMWith(userId: number) {
    try {
      const res = await fetch(`${this.store.apiURL}/channels/dm/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.store.token}`,
        },
        body: JSON.stringify({}),
      });

      if (!res.ok) {
        const errorText = await res.text();
        this.log('Failed to create DM', errorText);
        return null;
      }

      const data = await res.json();
      const channel = data?.data ?? data;

      if (channel && channel.id && !this.getChannel(channel.id)) {
        this._setChannels([channel, ...this.channels]);
      }

      if (channel) {
        this.setActiveChannel(channel);
      }

      return channel || null;
    } catch (e) {
      this.log('Error creating DM', e);
      return null;
    }
  }

  async sendMessage(channelId: number, text: string) {
    let channel = this.getChannel(channelId) || this.activeChannel;
    if (!channel || Number(channel.id) !== Number(channelId)) {
      channel = await this.loadChannel(channelId);
    }
    if (!channel || !channel.id) {
      this.log('No valid channel found for sending message');
      return;
    }

    const me = Number(this.store.user.user?.id);
    const rawMembers = Array.isArray(channel?.members) ? channel.members : [];
    const memberIds: number[] = rawMembers
      .map((m: any) => Number(m?.id ?? m?.userId ?? m))
      .filter((n: any) => Number.isFinite(n));

    if (memberIds.length && !memberIds.includes(me)) {
      this.log('Not a member of the channel, abort send');
      return;
    }

    const ws = this.store.socket.ws;
    if (!ws || !ws.connected) {
      this.log('Socket not connected, fallback: POST to API if available');
      return;
    }

    const clientId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    try {
      const payload: any = { channelId, clientId };

      if (channel?.type === 'dm') {
        const otherUserId = channel.members.find((id: number) => id !== me);
        if (!otherUserId) throw new Error('No recipient found');

        const cipher: any = await this.store.e2ee.encrypt(otherUserId.toString(), 1, text);

        // konwersja ArrayBuffer → Base64
        let bodyB64: string;

        if (typeof cipher.body === "string") {
          // zakoduj binarny string do base64
          bodyB64 = btoa(cipher.body);
        } else if (cipher.body instanceof ArrayBuffer) {
          bodyB64 = arrayBufferToBase64(cipher.body);
        } else if (cipher.body instanceof Uint8Array) {
          bodyB64 = arrayBufferToBase64(cipher.body.buffer);
        } else if (ArrayBuffer.isView(cipher.body)) {
          bodyB64 = arrayBufferToBase64(cipher.body.buffer);
        } else {
          throw new Error("Nieobsługiwany typ cipher.body: " + typeof cipher.body);
        }

        console.log("✅ base64 ready:", bodyB64.substring(0, 50) + "...");

        // Wysyłamy tylko ciphertext + type
        payload.hashContent = {
          type: cipher.type,
          body: bodyB64,
        };
      } else {
        payload.content = text;
      }

      const optimistic: any = {
        channel: channelId,
        // Jeśli DM (secure) → zamiast plaintextu pokazujemy placeholder
        content: channel.type === 'dm' ? '🔐 secured' : text,
        createdAt: new Date().toISOString(),
        id: genID(),
        clientId,
        author: me,
      };

      this.addMessage(channelId, optimistic);
      ws.emit('channel:message', payload);
    } catch (e) {
      this.log('Failed to send channel message via socket', e);
    }
  }


  updateChannel(updated: any) {
    if (!updated || typeof updated.id === 'undefined') return;
    const idx = this.channels.findIndex((c: any) => String(c.id) === String(updated.id));
    if (idx !== -1) {
      this.channels[idx] = updated;
    } else {
      this.channels.unshift(updated);
    }
    if (this.activeChannel && String(this.activeChannel.id) === String(updated.id)) {
      this.setActiveChannel(updated);
    }
  }

  removeChannel(channelId: number) {
    this.channels = this.channels.filter((c: any) => String(c.id) !== String(channelId));
    if (this.activeChannel && String(this.activeChannel.id) === String(channelId)) {
      this.setActiveChannel(null);
    }
  }

  _getChannels(type: 'dm' | 'group') {
    return this.channels.filter(channel => channel.type === type);
  }

  getGroups() {
    return this._getChannels('group');
  }

  getDirectMessages() {
    return this._getChannels('dm');
  }

  getChannel(id: number) {
    return this.channels.find(channel => channel.id === id);
  }
}
